import React, { useState } from "react";
import { SlidersHorizontal, ChevronRight } from "lucide-react";
import { Card } from "../../components/ui/Card";
import AnalyticsFilterPanel from "../../components/analytics/AnalyticsFilterPanel";
import useAnalyticsFilterOptions from "../../components/analytics/useAnalyticsFilterOptions";
import useAnalyticsFilters from "../../components/analytics/useAnalyticsFilters";
import useAnalyticsBatches from "../../components/analytics/useAnalyticsBatches";
import { usePlantSelection } from "../../features/plants/PlantSelectionContext";
import useMachineAnalytics from "./useMachineAnalytics";
import MachineCard from "./components/MachineCard";

const MachineAnalytics = () => {
  const [filterOpen, setFilterOpen] = useState(false);
  const { selectedPlantId } = usePlantSelection();
  const { draftFilters, appliedFilters, changeFilter, applyFilters, resetFilters } = useAnalyticsFilters(selectedPlantId);
  const options = useAnalyticsFilterOptions(draftFilters);
  const batchData = useAnalyticsBatches(selectedPlantId);
  const metrics = useMachineAnalytics(appliedFilters, options.machines, batchData.rows, selectedPlantId);
  const machines = metrics.rows;
  const isLoading = options.isLoading || batchData.isLoading || metrics.isLoading;
  const error = options.error || batchData.error || metrics.error;
  const shift = options.shifts.find((item) => item.shift_id === appliedFilters.shift);
  const filterProps = {
    open: filterOpen, onClose: () => setFilterOpen(false), values: draftFilters,
    onChange: changeFilter, onApply: applyFilters, onReset: resetFilters, ...options,
  };

  return (
    <div className="px-3 sm:px-5">
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between pb-1">
            <h1 className="text-2xl font-semibold text-slate-900">Machine Analytics</h1>
            <button type="button" onClick={() => setFilterOpen((open) => !open)} aria-expanded={filterOpen} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
              <SlidersHorizontal className="h-4 w-4" />Filter
            </button>
          </div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-sm text-slate-400">
              <span>{options.lineOptions.find((option) => option.value === appliedFilters.line)?.label || appliedFilters.line || "All Lines"}</span>
              <ChevronRight className="h-3.5 w-3.5" /><span>{appliedFilters.machineType || "All Types"}</span>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="font-medium text-slate-700">{options.machines.find((machine) => machine.machine_id === appliedFilters.machine)?.machine_name || appliedFilters.machine || "All Machines"}</span>
            </div>
            {shift && <p className="text-xs text-slate-400">Selected Shift: <span className="rounded-md bg-sky-50 px-2 py-1 font-semibold text-sky-600">{shift.shift_name || shift.shift_id} {shift.start_time && shift.end_time ? `| ${shift.start_time} – ${shift.end_time}` : ""}</span></p>}
          </div>
          {error ? (
            <Card className="p-10 text-center text-sm text-rose-600" role="alert">Unable to load machine analytics. <button type="button" onClick={() => { batchData.refetch(); options.refetch(); }} className="underline">Retry</button></Card>
          ) : isLoading ? (
            <Card className="p-10 text-center text-sm text-slate-400" role="status">Loading machine analytics…</Card>
          ) : machines.length === 0 ? (
            <Card className="p-10 text-center text-sm text-slate-400">No machines match the selected filters.</Card>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">{machines.map((machine) => <MachineCard key={machine.id} machine={machine} filters={appliedFilters} />)}</div>
              <p className="mt-4 text-xs text-slate-500">Select a machine to view its current batch and parameter readings. — indicates a metric that has not been reported.</p>
            </>
          )}
        </div>
        <AnalyticsFilterPanel {...filterProps} className="hidden lg:block" />
      </div>
      {filterOpen && <div className="fixed inset-0 z-40 flex justify-end bg-black/30 lg:hidden" onClick={() => setFilterOpen(false)}><div className="h-full w-full max-w-xs overflow-y-auto bg-white p-4" onClick={(event) => event.stopPropagation()}><AnalyticsFilterPanel {...filterProps} className="border-0 bg-transparent p-0" /></div></div>}
    </div>
  );
};
export default MachineAnalytics;
